import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { ShieldCheck, Lock, CheckCircle2, Sparkles } from 'lucide-react';

export interface TurnstileWidgetRef {
  reset: () => void;
  getToken: () => string | null;
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (error: string) => void;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
  customSiteKey?: string;
  invisible?: boolean; // When true, runs completely in the background with only our site design
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (error: any) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'flexible';
          action?: string;
          cData?: string;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
      execute: (container?: string | HTMLElement, options?: any) => void;
    };
  }
}

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  (
    {
      onVerify,
      onExpire,
      onError,
      theme = 'auto',
      className = '',
      customSiteKey,
      invisible = false,
    },
    ref
  ) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const hasRenderedRef = useRef(false);

    // Keep callback references stable across re-renders
    const onVerifyRef = useRef(onVerify);
    const onExpireRef = useRef(onExpire);
    const onErrorRef = useRef(onError);

    useEffect(() => {
      onVerifyRef.current = onVerify;
    }, [onVerify]);

    useEffect(() => {
      onExpireRef.current = onExpire;
    }, [onExpire]);

    useEffect(() => {
      onErrorRef.current = onError;
    }, [onError]);

    const [token, setToken] = useState<string | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready' | 'verified' | 'disabled' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [siteKey, setSiteKey] = useState<string>(customSiteKey || '');
    const [mode, setMode] = useState<'managed' | 'non-interactive' | 'invisible'>('managed');

    // Reset method exposed via ref
    useImperativeHandle(ref, () => ({
      reset: () => {
        setToken(null);
        setStatus('ready');
        if (window.turnstile && widgetIdRef.current) {
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch (e) {
            console.warn('[Turnstile Reset Warn]', e);
            if (isEffectivelyInvisible) {
              setToken('invisible_fallback');
              setStatus('verified');
              if (onVerifyRef.current) {
                onVerifyRef.current('invisible_fallback');
              }
            }
          }
        } else if (isEffectivelyInvisible) {
          setToken('invisible_fallback');
          setStatus('verified');
          if (onVerifyRef.current) {
            onVerifyRef.current('invisible_fallback');
          }
        }
      },
      getToken: () => token,
    }));

    // 1. Fetch siteKey and mode if not supplied
    useEffect(() => {
      let isMounted = true;

      if (customSiteKey) {
        setSiteKey(customSiteKey);
        return;
      }

      const fetchConfig = async () => {
        try {
          const res = await fetch('/api/turnstile-config', { cache: 'no-store' });
          const data = await res.json();

          if (!isMounted) return;

          if (data && data.enabled && data.siteKey) {
            setSiteKey(data.siteKey);
            if (data.mode) {
              setMode(data.mode);
            }
          } else {
            setStatus('disabled');
            if (onVerifyRef.current) {
              onVerifyRef.current('disabled_bypass');
            }
          }
        } catch (err) {
          if (!isMounted) return;
          console.warn('[Turnstile Config Fetch Error]', err);
          setStatus('disabled');
          if (onVerifyRef.current) {
            onVerifyRef.current('disabled_bypass');
          }
        }
      };

      fetchConfig();

      return () => {
        isMounted = false;
      };
    }, [customSiteKey]);

    const isEffectivelyInvisible = invisible || mode === 'invisible';

    // 2. Render Widget Function (strictly executes once when script + DOM are ready)
    const renderTurnstile = () => {
      if (!mountRef.current || !window.turnstile || !siteKey || hasRenderedRef.current) return;

      try {
        hasRenderedRef.current = true;

        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {}
          widgetIdRef.current = null;
        }

        const container = mountRef.current;
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }

        const id = window.turnstile.render(container, {
          sitekey: siteKey,
          theme: theme,
          size: 'flexible',
          callback: (resToken: string) => {
            setToken(resToken);
            setStatus('verified');
            if (onVerifyRef.current) {
              onVerifyRef.current(resToken);
            }
          },
          'expired-callback': () => {
            setToken(null);
            setStatus('ready');
            if (onExpireRef.current) {
              onExpireRef.current();
            }
            // Auto-refresh token on expiration so user is never stuck with an expired token
            if (widgetIdRef.current && window.turnstile) {
              try {
                window.turnstile.reset(widgetIdRef.current);
              } catch (e) {
                console.warn('[Turnstile Expire Reset Error]', e);
              }
            } else if (isEffectivelyInvisible) {
              setToken('invisible_fallback');
              setStatus('verified');
              if (onVerifyRef.current) {
                onVerifyRef.current('invisible_fallback');
              }
            }
          },
          'error-callback': (err: any) => {
            console.warn('[Turnstile Error Callback]', err);
            // In invisible mode, fail gracefully so it never blocks real patients
            if (isEffectivelyInvisible) {
              setToken('invisible_fallback');
              setStatus('verified');
              if (onVerifyRef.current) {
                onVerifyRef.current('invisible_fallback');
              }
            } else {
              setStatus('error');
              const msg = 'Turnstile doğrulaması başarısız oldu veya domain yetkisi yok.';
              setErrorMessage(msg);
              if (onErrorRef.current) {
                onErrorRef.current(msg);
              }
            }
          },
        });

        widgetIdRef.current = id;
        setStatus('ready');
      } catch (e: any) {
        console.error('[Turnstile Render Error]', e);
        hasRenderedRef.current = false;
        if (isEffectivelyInvisible) {
          setToken('invisible_fallback');
          setStatus('verified');
          if (onVerifyRef.current) {
            onVerifyRef.current('invisible_fallback');
          }
        } else {
          setStatus('error');
          setErrorMessage(e.message || 'Turnstile başlatılamadı.');
        }
      }
    };

    // 3. Load Turnstile Script safely and mount
    useEffect(() => {
      if (!siteKey || status === 'disabled') return;

      hasRenderedRef.current = false;
      const SCRIPT_ID = 'cf-turnstile-script';
      let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.onload = () => {
          renderTurnstile();
        };
        script.onerror = () => {
          if (isEffectivelyInvisible) {
            setToken('invisible_fallback');
            setStatus('verified');
            if (onVerifyRef.current) {
              onVerifyRef.current('invisible_fallback');
            }
          } else {
            setStatus('error');
            setErrorMessage('Cloudflare Turnstile betiği yüklenemedi.');
          }
        };
        document.head.appendChild(script);
      } else if (window.turnstile) {
        renderTurnstile();
      } else {
        const interval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(interval);
            renderTurnstile();
          }
        }, 80);
        return () => clearInterval(interval);
      }

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {}
          widgetIdRef.current = null;
        }
        hasRenderedRef.current = false;
      };
    }, [siteKey, theme, isEffectivelyInvisible]);

    if (status === 'disabled') {
      return null;
    }

    const isVerified = Boolean(token && status === 'verified');

    // ==========================================
    // INVISIBLE / SİTE TASARIMI MODU (Zero Cloudflare UI, 100% Native Site Design)
    // ==========================================
    if (isEffectivelyInvisible) {
      return (
        <div className={`pt-1 ${className}`}>
          {/* Cloudflare iframe runs silently in background */}
          <div
            ref={mountRef}
            className="w-0 h-0 opacity-0 pointer-events-none absolute overflow-hidden"
            aria-hidden="true"
          />

          {/* Sitemizin Kendi Medikal Güvenlik İmzası */}
          <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-slate-50 via-amber-50/20 to-slate-50 border border-slate-200/80 rounded-xl text-[11px] text-slate-600">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-semibold text-slate-800">Akıllı Bot ve Spam Koruması</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
              <Lock className="w-2.5 h-2.5 text-amber-600" />
              <span>256-Bit SSL Şifreleme</span>
            </div>
          </div>
        </div>
      );
    }

    // ==========================================
    // STANDART / GÖRÜNÜR MOD (Temalı Widget)
    // ==========================================
    return (
      <div className={`space-y-2 ${className}`}>
        {/* Clinically Designed Header Bar */}
        <div className="flex items-center justify-between text-xs px-0.5">
          <div className="flex items-center gap-1.5">
            <div className={`p-1 rounded-md transition-colors ${isVerified ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-700'}`}>
              <ShieldCheck className="w-3.5 h-3.5" />
            </div>
            <span className="font-bold text-slate-800 text-[12px] tracking-tight">Güvenlik Doğrulaması</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
            <Lock className="w-2.5 h-2.5" />
            <span>256-Bit SSL Koruma</span>
          </div>
        </div>

        {/* Outer Themed Box */}
        <div
          className={`relative overflow-hidden transition-all duration-300 rounded-2xl border p-2.5 sm:p-3 flex flex-col items-center justify-center min-h-[72px] ${
            isVerified
              ? 'bg-emerald-50/40 border-emerald-300/80 shadow-xs'
              : status === 'error'
              ? 'bg-rose-50/40 border-rose-200'
              : 'bg-gradient-to-b from-slate-50/90 to-slate-100/60 border-slate-200/90 shadow-2xs'
          }`}
        >
          {/* Loading State */}
          {status === 'loading' && (
            <div className="flex items-center gap-2.5 text-xs text-slate-600 py-3 font-medium">
              <div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-amber-600 rounded-full animate-spin" />
              <span>Güvenlik kontrolü yapılıyor...</span>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="flex items-center justify-between w-full px-3 py-2 bg-white/90 border border-rose-200 rounded-xl text-xs text-rose-800 shadow-2xs">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium">{errorMessage || 'Doğrulama yüklenemedi.'}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  hasRenderedRef.current = false;
                  setStatus('loading');
                  renderTurnstile();
                }}
                className="px-2.5 py-1 bg-rose-100 hover:bg-rose-200 text-rose-900 rounded-lg font-semibold text-[10px] transition-colors cursor-pointer"
              >
                Yeniden Dene
              </button>
            </div>
          )}

          {/* Isolated DOM mount node for Cloudflare iframe */}
          <div
            ref={mountRef}
            className={`w-full flex justify-center ${status === 'loading' || status === 'error' ? 'hidden' : 'block'}`}
          />
        </div>

        {/* Clinically Designed Verified Pill */}
        {isVerified && (
          <div className="flex items-center justify-between px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-[11px] text-emerald-800 font-semibold animate-fadeIn">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>İnsan doğrulaması onaylandı. Formu gönderebilirsiniz.</span>
            </div>
            <span className="text-[10px] text-emerald-700/80 font-normal">Korumalı</span>
          </div>
        )}
      </div>
    );
  }
);
